<template>
    <div id="google-business-channels">
        <AppBreadCrumb :paths="[{ title: 'Google Business Reviews' }]" :select="true"></AppBreadCrumb>
        <div style="background-color: #ffffff" class="d-flex right-page">
            <div class="ml-3 mt-3" style="flex: 0 0 100%">
                <div class="big-title">{{ $t("googleBusiness.channels") }}</div>
                <div class="channels">
                    <div
                        class="story channel-add"
                        @click="createChannel"
                    >
                        <a-icon type="plus-circle" theme="filled" style="font-size: 30px" />
                        <span class="text-large font-weight-bolder mt-2" style="font-size: 20px; letter-spacing: 2px">
                            {{ $t("whatsappChannel.addChannel") }}
                        </span>
                    </div>
                    <div
                        class="channel-item"
                        :class="{ disabled: !isChannelAccessible(channel) }"
                        v-for="(channel, index) in channels"
                        @click="() => isChannelAccessible(channel) && editChannel(channel)"
                        :key="index"
                    >
                        <div class="icon-container">
                            <GoogleBusinessIcon />
                        </div>
                        <div class="name-container">
                            <span class="name">{{ channel.name.toUpperCase() }}</span>
                            <span class="channel-title" v-if="channel.payload && channel.payload.accountName">
                                {{ channel.payload.accountName }}
                            </span>
                        </div>

                        <div class="options">
                            <a-dropdown :trigger="['click']" placement="bottomRight">
                                <div class="icon-container ant-dropdown-link" @click="(e) => e.stopPropagation()">
                                    <span class="material-icons">more_horiz</span>
                                </div>
                                <template #overlay>
                                    <a-menu>
                                        <a-menu-item @click="copyChannelId(channel._id)" style="display:flex; gap: 5px">
                                            <i class="material-icons">content_copy</i>
                                            <span>{{ $t("message.copyChannelID") }}</span>
                                        </a-menu-item>
                                        <a-menu-item>
                                            <div class="icon-container" @click="showDeleteConfirmation(channel._id)">
                                                <md-icon style="margin-right: 10px">delete</md-icon>
                                                <span>{{ $t("chatbotv2.delete") }}</span>
                                            </div>
                                        </a-menu-item>
                                    </a-menu>
                                </template>
                            </a-dropdown>
                        </div>
                    </div>
                </div>
            </div>
        </div>

        <Modal2
            :title="$t('googleBusiness.connectToGoogleBusiness')"
            :description="$t('googleBusiness.connectToGoogleBusiness')"
            class-name="new-google-business-channel"
            :showFooter="false"
            @cancel="showCreateForm = false"
            v-if="showCreateForm"
        >
            <CreateGoogleBusinessChannel @cancel="showCreateForm = false" @ok="onChannelCreated" />
        </Modal2>

        <a-modal
            :visible="deleteModal"
            icon="exclamation-circle"
            :title="$t('googleBusiness.title')"
            @cancel="deleteModal = false"
        >
            <p>{{ $t("googleBusiness.deleteChannelInfo") }}</p>
            <template #footer>
                <a-button key="back" @click="deleteModal = false">
                    {{ $t("chatbotv2.dismiss") }}
                </a-button>
                <a-button key="submit" type="danger" @click="deleteGoogleBusinessChannel">
                    {{ $t("chatbotv2.delete") }}
                </a-button>
            </template>
        </a-modal>
    </div>
</template>

<script>
import { mapActions, mapState, mapGetters } from "vuex";
import _ from "lodash";
import CreateGoogleBusinessChannel from "./CreateGoogleBusinessChannel.vue";
import Modal2 from "../../../../components/common/Modal2.vue";
import AppBreadCrumb from "../../../../components/common/AppBreadCrumb.vue";
import GoogleBusinessIcon from "../../Chatbot/pages/integrations/svg/GoogleBusinessIcon.vue";

export default {
    name: "GoogleBusinessChannels",
    components: {
        GoogleBusinessIcon,
        CreateGoogleBusinessChannel,
        Modal2,
        AppBreadCrumb,
    },
    data() {
        return {
            deleteModal: false,
            showCreateForm: false,
            updatingChannel: null,
            deleteChannelId: null,
        };
    },
    computed: {
        ...mapState({
            channels: (state) => state.channel.channels.filter((x) => x.type === "google-business-reviews"),
            isAdmin: (state) => state.auth.adminUser !== null,
            user: (state) => state.auth.user,
        }),
    },
    methods: {
        ...mapGetters({
            isChannelVisibleForMe: "channel/isChannelVisibleForMe",
        }),
        ...mapActions({
            listChannels: "channel/listChannels",
            deleteChannel: "channel/deleteChannel",
        }),
        copyChannelId(channelId) {
            navigator.clipboard.writeText(channelId).then(() => {
                this.$message.success("Channel ID kopyalandı");
            }).catch(() => {
                this.$message.error("Channel ID kopyalanamadı");
            });
        },
        showDeleteConfirmation(id) {
            this.deleteModal = true;
            this.deleteChannelId = id;
        },
        createChannel() {
            this.showCreateForm = true;
        },
        onChannelCreated() {
            this.showCreateForm = false;
            this.listChannels();
        },
        deleteGoogleBusinessChannel() {
            this.deleteModal = false;
            this.deleteChannel(this.deleteChannelId);
        },
        editChannel(channel) {
            this.$router.push(`/integrations/google-business-reviews/${channel._id}/detail`);
        },
        isChannelAccessible(channel) {
            return this.isAdmin || _.get(this.user, "isOwner", false) || this.isChannelVisibleForMe(channel);
        },
    },
    mounted() {
        this.listChannels();
    },
};
</script>

<style lang="scss" scoped>
#google-business-channels {
    width: 100%;
    min-height: calc(100vh - 50px);
    background: var(--color-bg-default);
    .channel-item.disabled {
        opacity: 0.5;
        pointer-events: none;
    }

    .big-title {
        font-weight: bold;
        font-size: 1.5rem;
        margin: 1rem 0;
    }

    .channels {
        display: flex;
        align-items: flex-start;
        padding-bottom: 1rem;
        flex-wrap: wrap;
        gap: 1rem;

        .story {
            background: #fff;
            border: 1px solid #a4a4a4;
            border-radius: 4px;
            padding: 1rem;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            width: 280px;
            height: 260px;
            min-width: 250px;
            max-width: 250px;
            margin-top: 10px;

            &:hover {
                border: 1px solid #4285f4;
            }
        }

        .channel-item {
            position: relative;
            background: #fff;
            border: 1px solid #a4a4a4;
            border-radius: 4px;
            padding: 1rem;
            cursor: pointer;
            transition: all 0.3s ease-in-out;
            width: 280px;
            height: 260px;
            min-width: 250px;
            max-width: 250px;
            text-align: center;
            margin-top: 10px;

            &:hover {
                border: 1px solid #4285f4;
                transform: translateY(-10px);
                box-shadow: -4px 22px 11px -5px rgba(0, 0, 0, 0.1);

                span {
                    color: #4285f4;
                }
            }

            .icon-container {
                width: 170px;
                display: flex;
                margin: 0 auto;
                align-items: center;
                justify-content: center;
                border-radius: 6px;
                overflow: hidden;
                position: relative;
                background: #fff;
                padding: 10px;

                svg {
                    width: 100px;
                    height: 100px;
                }
            }

            .name-container {
                display: flex;
                flex-direction: column;
                gap: 5px;
                font-size: 20px;
                font-weight: bold;
                margin-top: 10px !important;

                .channel-title {
                    font-size: 14px;
                    font-weight: normal;
                    color: #a4a4a4;
                }
            }

            .options {
                position: absolute;
                top: 5px;
                right: 5px;

                .icon-container {
                    background-color: transparent;
                    width: 25px;
                    height: 35px;
                    margin: 0;
                    padding: 0;
                    display: flex;
                    justify-content: center;
                    align-items: center;
                    padding: 3px;
                }
            }
        }

        .channel-add {
            display: flex;
            flex-direction: column;
            align-items: center;
            justify-content: center;
            background: #4285f4;
            border-color: #4285f4;
            color: #fff;

            i {
                margin: 0;
            }
        }
    }
}
</style>
